import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  SimpleGrid,
  Text,
  Heading,
  Button,
  Icon,
  Card,
  CardBody,
  CardHeader,
  useToast,
  useColorModeValue,
  Input,
  InputGroup,
  InputLeftElement,
  FormControl,
  FormLabel,
  Textarea,
  Select,
  Checkbox,
  Alert,
  AlertIcon,
  Image,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  List,
  ListItem,
  ListIcon,
  Spinner,
  Avatar,
  Flex,
} from '@chakra-ui/react';
import { 
  FaCheck,
  FaRocket,
  FaChartLine,
  FaUsers,
  FaShieldAlt,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaGlobe,
  FaBuilding,
  FaFlask,
  FaDollarSign,
  FaCalendarAlt,
  FaHandshake,
  FaStar,
  FaCertificate,
  FaCrown,
  FaInfinity,
  FaSearch
} from 'react-icons/fa';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';

export default function LabSignup() {
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [labData, setLabData] = useState(null);
  const [labLoading, setLabLoading] = useState(true);
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { labSlug } = useParams();
  
  const bgGradient = useColorModeValue(
    'linear(to-br, blue.50, purple.50)',
    'linear(to-br, gray.800, gray.900)'
  );
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('gray.800', 'white');

  const benefits = [
    {
      icon: FaUsers,
      title: "Expand Patient Reach",
      description: "Connect with thousands of patients seeking lab services in your area",
      color: "blue"
    },
    {
      icon: FaChartLine,
      title: "Increase Revenue",
      description: "Generate more business through our growing network of healthcare providers",
      color: "green"
    },
    {
      icon: FaRocket,
      title: "Digital Presence",
      description: "Professional lab profile with your branding, services, and pricing",
      color: "purple"
    },
    {
      icon: FaShieldAlt,
      title: "Verified Listings",
      description: "Build trust with verified credentials and patient reviews",
      color: "orange"
    }
  ];

  const features = [
    "Professional lab profile with your logo and branding",
    "List all your test offerings with transparent pricing",
    "Direct patient inquiries and booking requests",
    "Integration with phlebotomist network",
    "Patient review and rating system",
    "Analytics dashboard to track performance",
    "Mobile-optimized presence",
    "SEO-optimized listing for local search"
  ];

  const labTypes = [
    "Full-Service Clinical Laboratory",
    "Specialty Reference Laboratory",
    "Molecular Diagnostics Laboratory",
    "General Clinical Laboratory",
    "Research & Clinical Laboratory",
    "Point-of-Care Testing Facility",
    "Other"
  ];

  const certificationOptions = [
    "CLIA Certified",
    "CAP Accredited",
    "ISO 15189",
    "ASHI Accredited",
    "GLP Compliant",
    "AABB Accredited",
    "Other"
  ];

  const interestOptions = [
    "Patient Direct Testing",
    "Provider Partnerships",
    "Home Collection Services",
    "Corporate Wellness Programs",
    "Research Collaborations",
    "Insurance Billing Support"
  ];

  const pricingPlans = [
    {
      id: 'starter',
      name: 'Starter',
      price: 99,
      period: 'month',
      description: 'Standard placement in our network',
      badge: 'STANDARD PLACEMENT',
      placement: 'Standard',
      features: [
        'Professional test directory listing',
        'Unlimited test types & cash prices',
        'Insurance acceptance display',
        'Standard placement in mobile phlebotomist selection',
        'Basic analytics dashboard',
        'Email support',
        'Mobile-optimized directory',
        'Patient review system'
      ],
      color: 'blue',
      popular: false,
      stripePrice: 'price_1ABC123starter' // Replace with your actual Stripe Price ID
    },
    {
      id: 'professional',
      name: 'Professional',
      price: 299,
      period: 'month',
      description: 'Priority placement for more blood draws',
      badge: 'PRIORITY PLACEMENT',
      placement: 'Priority',
      features: [
        'Everything in Starter',
        'Priority placement in mobile phlebotomist selection',
        'Higher visibility in test directory',
        'Preferred lab status for new draws',
        'Advanced analytics & reporting',
        'Priority support',
        'Custom branding options',
        'Enhanced FedEx shipping rates',
        'Lead management tools'
      ],
      color: 'purple',
      popular: true,
      stripePrice: 'price_1DEF456professional' // Replace with your actual Stripe Price ID
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 999,
      period: 'month',
      description: 'Highest placement priority & best shipping rates',
      badge: 'HIGHEST PLACEMENT',
      placement: 'Highest',
      features: [
        'Everything in Professional',
        'Highest placement priority for ALL new blood draws',
        'Premium criteria matching for mobile phlebotomists',
        'Best available FedEx shipping rates (up to 40% savings)',
        'Dedicated account manager',
        'Custom integrations',
        'White-label solutions',
        'Advanced reporting suite',
        '24/7 priority support',
        'Revenue sharing opportunities'
      ],
      color: 'orange',
      popular: false,
      stripePrice: 'price_1GHI789enterprise' // Replace with your actual Stripe Price ID
    }
  ];

  // Fetch lab data from URL parameters or state
  useEffect(() => {
    const fetchLabData = async () => {
      try {
        setLabLoading(true);
        
        console.log('LabSignup: Fetching lab data...');
        console.log('Location state:', location.state);
        // Check if lab data is passed via location state first
        if (location.state?.labData) {
          setLabData(location.state.labData);
          return;
        }
        
        // Check URL search params for labId
        const urlParams = new URLSearchParams(location.search);
        const labIdFromUrl = urlParams.get('labId');
        console.log('Lab ID from URL:', labIdFromUrl);
        
        if (location.state?.labId || labIdFromUrl) {
          console.log('Fetching lab by ID:', location.state?.labId || labIdFromUrl);
          const { data, error } = await supabase
            .from('labs')
            .select('*')
            .eq('id', location.state?.labId || labIdFromUrl)
            .single();
            
          if (error) throw error;
          setLabData(data);
        } else if (labSlug) {
          // If labSlug is in URL params, fetch by short_slug
          const { data, error } = await supabase
            .from('labs')
            .select('*')
            .eq('short_slug', labSlug)
            .single();
            
          if (error) throw error;
          setLabData(data);
        } else {
        }
      } catch (error) {
        console.error('Error fetching lab data:', error);
      } finally {
        setLabLoading(false);
      }
    };

    fetchLabData();
  }, [labSlug, location.state, location.search]);

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    
    // Navigate to checkout with plan details and lab data
    navigate('/lab-checkout', { 
      state: { 
        plan: plan,
        labData: labData // This will be null for generic signup, or the actual lab data if available
      }
    });
  };



  return (
    <Box minHeight="100vh" bg={bgGradient}>
      {/* Hero Section */}
      <Box bg={cardBg} borderBottom="1px" borderColor={borderColor} py={16}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">
            {/* Left Column - Main Content */}
            <VStack spacing={8} textAlign={{ base: "center", lg: "left" }}>
              <Badge colorScheme="blue" size="lg" px={4} py={2} borderRadius="full">
                Partner with Us
              </Badge>
              {labData?.trial_days > 0 && (
                <Badge colorScheme="purple" size="sm" px={3} py={1} borderRadius="full">
                  {labData.trial_days}-day free trial
                </Badge>
              )}
              <Heading size="2xl" color={headingColor}>
                Join the Leading Laboratory Network
              </Heading>
              <Text fontSize="xl" color={textColor}>
                Connect with more patients, grow your business, and become part of the most trusted 
                laboratory directory serving healthcare providers nationwide.
              </Text>
              
              {/* Call to Action */}
              <Button
                size="lg"
                colorScheme="blue"
                leftIcon={<Icon as={FaRocket} />}
                onClick={() => {
                  const pricingSection = document.getElementById('pricing-plans');
                  pricingSection?.scrollIntoView({ behavior: 'smooth' });
                }}
                px={8}
                py={6}
                fontSize="lg"
                fontWeight="bold"
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                transition="all 0.2s"
              >
                Get Started Today
              </Button>
              
              {/* Stats */}
              <SimpleGrid columns={{ base: 2, md: 2 }} spacing={6} w="full">
                <Stat textAlign="center">
                  <StatNumber color="blue.600" fontSize="2xl">75K+</StatNumber>
                  <StatLabel color={textColor} fontSize="sm">Monthly Searches</StatLabel>
                  <StatHelpText color="green.500" fontSize="xs">Growing 30% monthly</StatHelpText>
                </Stat>
                <Stat textAlign="center">
                  <StatNumber color="green.600" fontSize="2xl">350+</StatNumber>
                  <StatLabel color={textColor} fontSize="sm">Partner Labs</StatLabel>
                  <StatHelpText color="green.500" fontSize="xs">Nationwide coverage</StatHelpText>
                </Stat>
                <Stat textAlign="center">
                  <StatNumber color="purple.600" fontSize="2xl">25K+</StatNumber>
                  <StatLabel color={textColor} fontSize="sm">Healthcare Providers</StatLabel>
                  <StatHelpText color="green.500" fontSize="xs">Verified network</StatHelpText>
                </Stat>
                <Stat textAlign="center">
                  <StatNumber color="orange.600" fontSize="2xl">4.9★</StatNumber>
                  <StatLabel color={textColor} fontSize="sm">Average Rating</StatLabel>
                  <StatHelpText color="green.500" fontSize="xs">Patient satisfaction</StatHelpText>
                </Stat>
              </SimpleGrid>
            </VStack>

            {/* Right Column - Lab Details */}
            <Box>
              {labLoading ? (
                <Card bg="gray.50" p={8}>
                  <VStack spacing={4}>
                    <Spinner size="lg" color="blue.500" />
                    <Text color={textColor}>Loading lab information...</Text>
                  </VStack>
                </Card>
              ) : labData ? (
                <Card bg="white" border="2px" borderColor="blue.200" borderRadius="xl" boxShadow="xl">
                  <CardBody p={6}>
                    <VStack spacing={4} align="start">
                      <HStack spacing={4} w="full">
                        <Avatar 
                          src={labData.logo_url} 
                          name={labData.name}
                          size="lg"
                          bg="blue.500"
                        />
                        <VStack align="start" spacing={1} flex={1}>
                          <Badge colorScheme="green" size="sm">Signing Up</Badge>
                          <Heading size="md" color={headingColor}>{labData.name}</Heading>
                          <Text fontSize="sm" color={textColor}>{labData.address}</Text>
                        </VStack>
                      </HStack>
                      
                      <Divider />
                      
                      <VStack spacing={3} w="full" align="start">
                        <Text fontWeight="bold" color={headingColor}>Laboratory Information</Text>
                        
                        {labData.contact_email && (
                          <HStack spacing={2}>
                            <Icon as={FaEnvelope} color="gray.500" />
                            <Text fontSize="sm" color={textColor}>{labData.contact_email}</Text>
                          </HStack>
                        )}
                        
                        {labData.contact_phone && (
                          <HStack spacing={2}>
                            <Icon as={FaPhone} color="gray.500" />
                            <Text fontSize="sm" color={textColor}>{labData.contact_phone}</Text>
                          </HStack>
                        )}
                        
                        <HStack spacing={2}>
                          <Icon as={FaBuilding} color="gray.500" />
                          <Text fontSize="sm" color={textColor}>
                            {labData.is_active ? 'Active Laboratory' : 'Inactive Laboratory'}
                          </Text>
                        </HStack>
                        
                        {labData.short_slug && (
                          <HStack spacing={2}>
                            <Icon as={FaGlobe} color="gray.500" />
                            <Text fontSize="sm" color={textColor}>Slug: {labData.short_slug}</Text>
                          </HStack>
                        )}
                      </VStack>
                      
                      <Alert status="info" borderRadius="md" size="sm">
                        <AlertIcon />
                        <Text fontSize="sm">
                          This laboratory is signing up for a partnership plan
                        </Text>
                      </Alert>
                    </VStack>
                  </CardBody>
                </Card>
              ) : (
                <Card bg="gray.50" border="2px" borderColor="gray.200" borderRadius="xl">
                  <CardBody p={6}>
                    <VStack spacing={4} textAlign="center">
                      <Icon as={FaBuilding} boxSize={8} color="gray.400" />
                      <VStack spacing={2}>
                        <Text fontWeight="bold" color={headingColor}>No Lab Selected</Text>
                        <Text fontSize="sm" color={textColor}>
                          Lab information will appear here when accessed from a lab directory
                        </Text>
                      </VStack>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => navigate('/lab-directory')}
                        leftIcon={<Icon as={FaSearch} />}
                      >
                        Browse Labs
                      </Button>
                    </VStack>
                  </CardBody>
                </Card>
              )}
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={12}>
          <VStack spacing={4} textAlign="center">
            <Heading size="xl" color={headingColor}>
              Why Partner With Us?
            </Heading>
            <Text fontSize="lg" color={textColor} maxW="2xl">
              Join hundreds of laboratories already growing their business through our platform
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8}>
            {benefits.map((benefit, index) => (
              <Card key={index} bg={cardBg} borderColor={borderColor} h="full">
                <CardBody textAlign="center">
                  <VStack spacing={4}>
                    <Box
                      bg={`${benefit.color}.100`}
                      p={4}
                      borderRadius="full"
                      border="2px"
                      borderColor={`${benefit.color}.200`}
                    >
                      <Icon as={benefit.icon} boxSize={8} color={`${benefit.color}.600`} />
                    </Box>
                    <Heading size="md" color={headingColor}>
                      {benefit.title}
                    </Heading>
                    <Text color={textColor} fontSize="sm">
                      {benefit.description}
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </VStack>
      </Container>

      {/* Pricing Section */}
      <Container maxW="container.xl" py={16} id="pricing-plans">
        <VStack spacing={12}>
          <VStack spacing={4} textAlign="center">
            <Badge colorScheme="green" size="lg" px={4} py={2} borderRadius="full">
              Choose Your Plan
            </Badge>
            <Heading size="xl" color={headingColor}>
              Simple, Transparent Pricing
            </Heading>
            <Text fontSize="lg" color={textColor} maxW="2xl">
              Select the plan that best fits your laboratory's needs. No hidden fees, cancel anytime.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="full">
            {pricingPlans.map((plan, index) => (
              <Card 
                key={plan.id} 
                bg={cardBg} 
                borderColor={plan.popular ? `${plan.color}.500` : borderColor}
                border="2px"
                position="relative"
                _hover={{ transform: 'translateY(-4px)', boxShadow: 'xl' }}
                transition="all 0.3s"
                h="full"
              >
                {plan.popular && (
                  <Box
                    position="absolute"
                    top="-12px"
                    left="50%"
                    transform="translateX(-50%)"
                    bg={`${plan.color}.500`}
                    color="white"
                    px={4}
                    py={1}
                    borderRadius="full"
                    fontSize="sm"
                    fontWeight="bold"
                  >
                    Most Popular
                  </Box>
                )}
                
                <CardBody p={8} textAlign="center">
                  <VStack spacing={6} h="full">
                    <VStack spacing={2}>
                      <Box
                        bg={`${plan.color}.100`}
                        p={3}
                        borderRadius="full"
                        border="2px"
                        borderColor={`${plan.color}.200`}
                      >
                        <Icon 
                          as={plan.id === 'enterprise' ? FaCrown : plan.id === 'professional' ? FaStar : FaBuilding} 
                          boxSize={6} 
                          color={`${plan.color}.600`} 
                        />
                      </Box>
                      <Badge 
                        colorScheme={plan.color} 
                        size="md" 
                        px={3} 
                        py={1} 
                        borderRadius="full"
                        fontWeight="bold"
                      >
                        {plan.badge}
                      </Badge>
                      <Heading size="lg" color={headingColor}>
                        {plan.name}
                      </Heading>
                      <Text color={textColor} fontSize="sm" textAlign="center">
                        {plan.description}
                      </Text>
                    </VStack>

                    <VStack spacing={1}>
                      <HStack align="baseline" justify="center">
                        <Text fontSize="4xl" fontWeight="bold" color={`${plan.color}.600`}>
                          ${plan.price}
                        </Text>
                        <Text color={textColor}>/{plan.period}</Text>
                      </HStack>
                      <Text fontSize="sm" color={textColor}>
                        {labData?.trial_days > 0
                          ? `${labData.trial_days}-day free trial, then billed monthly`
                          : 'Billed monthly, cancel anytime'}
                      </Text>
                    </VStack>

                    <VStack spacing={3} align="start" flex="1">
                      {plan.features.map((feature, featureIndex) => (
                        <HStack key={featureIndex} align="start">
                          <Icon as={FaCheck} color="green.500" mt={1} boxSize={3} />
                          <Text fontSize="sm" color={textColor} textAlign="left">
                            {feature}
                          </Text>
                        </HStack>
                      ))}
                    </VStack>

                    <Button
                      colorScheme={plan.color}
                      size="lg"
                      width="full"
                      onClick={() => handlePlanSelect(plan)}
                      variant={plan.popular ? "solid" : "outline"}
                      fontWeight="bold"
                      isLoading={labLoading}
                      loadingText="Loading lab info..."
                      _hover={{ 
                        transform: 'translateY(-2px)', 
                        boxShadow: 'lg',
                        ...(plan.popular ? {} : { bg: `${plan.color}.50` })
                      }}
                    >
                      {plan.popular ? 'Get Started' : 'Choose Plan'}
                    </Button>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>

          <VStack spacing={4} textAlign="center">
            <Text color={textColor} fontSize="sm">
              {labData?.trial_days > 0
                ? `Includes a ${labData.trial_days}-day free trial • No setup fees • Cancel anytime`
                : 'No setup fees • Cancel anytime'}
            </Text>
            <HStack spacing={6}>
              <Text fontSize="sm" color={textColor}>
                Need a custom plan? 
              </Text>
              <Button
                variant="link"
                colorScheme="blue"
                onClick={() => window.location.href = 'mailto:sales@company.com'}
              >
                Contact Sales
              </Button>
            </HStack>
          </VStack>
        </VStack>
      </Container>

      {/* Professional Laboratory Profile Section */}
      <Box bg={cardBg} py={16}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">
            <VStack align="start" spacing={6}>
              <VStack align="start" spacing={4}>
                <Badge colorScheme="green" size="md">
                  Everything You Need
                </Badge>
                <Heading size="xl" color={headingColor}>
                  Professional Laboratory Profile
                </Heading>
                <Text color={textColor} fontSize="lg">
                  Create a comprehensive laboratory profile that showcases your capabilities, 
                  builds trust with patients, and drives business growth.
                </Text>
              </VStack>
              
              <List spacing={3}>
                {features.map((feature, index) => (
                  <ListItem key={index}>
                    <HStack align="start">
                      <ListIcon as={FaCheck} color="green.500" mt={1} />
                      <Text color={textColor}>{feature}</Text>
                    </HStack>
                  </ListItem>
                ))}
              </List>
            </VStack>

            <Box>
              <Image
                src="https://images.unsplash.com/photo-1582719471384-894fbb16e074?w=600&h=400&fit=crop&auto=format"
                alt="Professional laboratory profile"
                borderRadius="lg"
                boxShadow="xl"
              />
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Test Directory Benefits */}
      <Box bg={cardBg} py={16}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Badge colorScheme="blue" size="lg" px={4} py={2} borderRadius="full">
                Test Directory Benefits
              </Badge>
              <Heading size="xl" color={headingColor}>
                Complete Test Directory for Your Customers
              </Heading>
              <Text fontSize="lg" color={textColor} maxW="3xl">
                Your customers can see exactly what tests you offer, cash prices, and insurance accepted - 
                making it easy for them to choose your laboratory for their testing needs.
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">
              <VStack align="start" spacing={6}>
                <VStack align="start" spacing={4}>
                  <Heading size="lg" color={headingColor}>
                    What Your Customers See
                  </Heading>
                  <List spacing={4}>
                    <ListItem>
                      <HStack align="start">
                        <ListIcon as={FaFlask} color="blue.500" mt={1} />
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="bold" color={headingColor}>Complete Test Catalog</Text>
                          <Text fontSize="sm" color={textColor}>
                            Every test type you offer with detailed descriptions and requirements
                          </Text>
                        </VStack>
                      </HStack>
                    </ListItem>
                    
                    <ListItem>
                      <HStack align="start">
                        <ListIcon as={FaDollarSign} color="green.500" mt={1} />
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="bold" color={headingColor}>Transparent Cash Pricing</Text>
                          <Text fontSize="sm" color={textColor}>
                            Clear, upfront pricing so patients know exactly what they'll pay
                          </Text>
                        </VStack>
                      </HStack>
                    </ListItem>
                    
                    <ListItem>
                      <HStack align="start">
                        <ListIcon as={FaShieldAlt} color="purple.500" mt={1} />
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="bold" color={headingColor}>Insurance Acceptance</Text>
                          <Text fontSize="sm" color={textColor}>
                            Which insurance plans you accept for each test type
                          </Text>
                        </VStack>
                      </HStack>
                    </ListItem>
                    
                    <ListItem>
                      <HStack align="start">
                        <ListIcon as={FaCertificate} color="orange.500" mt={1} />
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="bold" color={headingColor}>Lab Certifications</Text>
                          <Text fontSize="sm" color={textColor}>
                            Your accreditations and certifications build trust
                          </Text>
                        </VStack>
                      </HStack>
                    </ListItem>
                  </List>
                </VStack>
              </VStack>

              <Box>
                <Card bg="gray.50" border="2px" borderColor="gray.200" borderRadius="xl">
                  <CardBody p={6}>
                    <VStack spacing={4} align="start">
                      <HStack justify="space-between" w="full">
                        <Text fontWeight="bold" color={headingColor}>Sample Test Directory</Text>
                        <Badge colorScheme="green">Live Example</Badge>
                      </HStack>
                      
                      <VStack spacing={3} w="full" align="start">
                        <Box p={3} bg="white" borderRadius="md" w="full">
                          <HStack justify="space-between">
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="medium">Complete Blood Count (CBC)</Text>
                              <Text fontSize="sm" color="gray.600">Code: 85025</Text>
                            </VStack>
                            <VStack align="end" spacing={0}>
                              <Text fontWeight="bold" color="green.600">$45</Text>
                              <Text fontSize="xs" color="gray.500">Cash Price</Text>
                            </VStack>
                          </HStack>
                          <HStack mt={2} spacing={2}>
                            <Badge size="sm" colorScheme="blue">Most Insurance</Badge>
                            <Badge size="sm" colorScheme="purple">Medicare</Badge>
                          </HStack>
                        </Box>
                        
                        <Box p={3} bg="white" borderRadius="md" w="full">
                          <HStack justify="space-between">
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="medium">Lipid Panel</Text>
                              <Text fontSize="sm" color="gray.600">Code: 80061</Text>
                            </VStack>
                            <VStack align="end" spacing={0}>
                              <Text fontWeight="bold" color="green.600">$65</Text>
                              <Text fontSize="xs" color="gray.500">Cash Price</Text>
                            </VStack>
                          </HStack>
                          <HStack mt={2} spacing={2}>
                            <Badge size="sm" colorScheme="blue">Most Insurance</Badge>
                            <Badge size="sm" colorScheme="green">Self Pay</Badge>
                          </HStack>
                        </Box>
                        
                        <Box p={3} bg="white" borderRadius="md" w="full">
                          <HStack justify="space-between">
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="medium">Thyroid Stimulating Hormone</Text>
                              <Text fontSize="sm" color="gray.600">Code: 84443</Text>
                            </VStack>
                            <VStack align="end" spacing={0}>
                              <Text fontWeight="bold" color="green.600">$55</Text>
                              <Text fontSize="xs" color="gray.500">Cash Price</Text>
                            </VStack>
                          </HStack>
                          <HStack mt={2} spacing={2}>
                            <Badge size="sm" colorScheme="blue">Most Insurance</Badge>
                            <Badge size="sm" colorScheme="purple">Medicare</Badge>
                            <Badge size="sm" colorScheme="orange">Medicaid</Badge>
                          </HStack>
                        </Box>
                      </VStack>
                    </VStack>
                  </CardBody>
                </Card>
              </Box>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Mobile Phlebotomist Integration */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={12}>
          <VStack spacing={4} textAlign="center">
            <Badge colorScheme="purple" size="lg" px={4} py={2} borderRadius="full">
              Mobile Phlebotomist Network
            </Badge>
            <Heading size="xl" color={headingColor}>
              Get Selected for Blood Draws from Our Mobile Network
            </Heading>
            <Text fontSize="lg" color={textColor} maxW="3xl">
              When mobile phlebotomists in our network collect blood samples, your laboratory 
              gets prioritized based on your plan level, location, and test capabilities.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={8} w="full">
            <Card bg={cardBg} borderColor="blue.200" border="2px">
              <CardBody p={6} textAlign="center">
                <VStack spacing={4}>
                  <Box bg="blue.100" p={4} borderRadius="full">
                    <Icon as={FaUsers} boxSize={8} color="blue.600" />
                  </Box>
                  <VStack spacing={2}>
                    <Text fontWeight="bold" color={headingColor}>Mobile Phlebotomist</Text>
                    <Text fontSize="sm" color={textColor}>
                      Collects blood sample from patient at home or office
                    </Text>
                  </VStack>
                  <Badge colorScheme="blue" size="lg" borderRadius="full">
                    Step 1: Collection
                  </Badge>
                </VStack>
              </CardBody>
            </Card>

            <Card bg={cardBg} borderColor="purple.200" border="2px">
              <CardBody p={6} textAlign="center">
                <VStack spacing={4}>
                  <Box bg="purple.100" p={4} borderRadius="full">
                    <Icon as={FaSearch} boxSize={8} color="purple.600" />
                  </Box>
                  <VStack spacing={2}>
                    <Text fontWeight="bold" color={headingColor}>Lab Selection</Text>
                    <Text fontSize="sm" color={textColor}>
                      System selects your lab based on placement priority, location, and test type
                    </Text>
                  </VStack>
                  <Badge colorScheme="purple" size="lg" borderRadius="full">
                    Step 2: Selection
                  </Badge>
                </VStack>
              </CardBody>
            </Card>

            <Card bg={cardBg} borderColor="green.200" border="2px">
              <CardBody p={6} textAlign="center">
                <VStack spacing={4}>
                  <Box bg="green.100" p={4} borderRadius="full">
                    <Icon as={FaFlask} boxSize={8} color="green.600" />
                  </Box>
                  <VStack spacing={2}>
                    <Text fontWeight="bold" color={headingColor}>Sample Processing</Text>
                    <Text fontSize="sm" color={textColor}>
                      You receive the sample with optimized FedEx shipping and process results
                    </Text>
                  </VStack>
                  <Badge colorScheme="green" size="lg" borderRadius="full">
                    Step 3: Processing
                  </Badge>
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Placement Priority Visualization */}
          <Card bg="gray.50" border="2px" borderColor="gray.200" w="full">
            <CardBody p={8}>
              <VStack spacing={6}>
                <Heading size="lg" color={headingColor} textAlign="center">
                  How Placement Priority Works
                </Heading>
                
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} w="full">
                  <VStack spacing={3} textAlign="center">
                    <Badge colorScheme="blue" size="lg" px={4} py={2}>STANDARD</Badge>
                    <Text fontSize="lg" fontWeight="bold" color="blue.600">$99/month</Text>
                    <Text fontSize="sm" color={textColor}>
                      Standard placement in selection algorithm
                    </Text>
                    <Box w="full" h={4} bg="blue.200" borderRadius="md">
                      <Box w="30%" h="full" bg="blue.500" borderRadius="md" />
                    </Box>
                    <Text fontSize="xs" color="blue.600">30% selection priority</Text>
                  </VStack>
                  
                  <VStack spacing={3} textAlign="center">
                    <Badge colorScheme="purple" size="lg" px={4} py={2}>PRIORITY</Badge>
                    <Text fontSize="lg" fontWeight="bold" color="purple.600">$299/month</Text>
                    <Text fontSize="sm" color={textColor}>
                      Higher placement with preferred status
                    </Text>
                    <Box w="full" h={4} bg="purple.200" borderRadius="md">
                      <Box w="65%" h="full" bg="purple.500" borderRadius="md" />
                    </Box>
                    <Text fontSize="xs" color="purple.600">65% selection priority</Text>
                  </VStack>
                  
                  <VStack spacing={3} textAlign="center">
                    <Badge colorScheme="orange" size="lg" px={4} py={2}>HIGHEST</Badge>
                    <Text fontSize="lg" fontWeight="bold" color="orange.600">$999/month</Text>
                    <Text fontSize="sm" color={textColor}>
                      Maximum priority for all new draws
                    </Text>
                    <Box w="full" h={4} bg="orange.200" borderRadius="md">
                      <Box w="95%" h="full" bg="orange.500" borderRadius="md" />
                    </Box>
                    <Text fontSize="xs" color="orange.600">95% selection priority</Text>
                  </VStack>
                </SimpleGrid>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Container>

      {/* Results Platform */}
      <Box bg={cardBg} py={16}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Badge colorScheme="green" size="lg" px={4} py={2} borderRadius="full">
                Results Platform
              </Badge>
              <Heading size="xl" color={headingColor}>
                Phlebotomists See Results on Our Platform
              </Heading>
              <Text fontSize="lg" color={textColor} maxW="3xl">
                When you upload results to our platform, mobile phlebotomists and patients 
                can access them securely through our integrated results portal.
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">
              <Box>
                <Card bg="white" border="2px" borderColor="green.200" borderRadius="xl" boxShadow="xl">
                  <CardBody p={6}>
                    <VStack spacing={4} align="start">
                      <HStack justify="space-between" w="full">
                        <Text fontWeight="bold" color={headingColor}>Patient Results Portal</Text>
                        <Badge colorScheme="green">Live Platform</Badge>
                      </HStack>
                      
                      <VStack spacing={3} w="full" align="start">
                        <Box p={4} bg="green.50" borderRadius="md" w="full" border="1px" borderColor="green.200">
                          <HStack justify="space-between">
                            <VStack align="start" spacing={1}>
                              <Text fontWeight="medium">Complete Blood Count</Text>
                              <Text fontSize="sm" color="gray.600">Collected: March 15, 2024</Text>
                            </VStack>
                            <Badge colorScheme="green">Ready</Badge>
                          </HStack>
                          <HStack mt={3} spacing={4}>
                            <Button size="sm" colorScheme="green" leftIcon={<Icon as={FaCheck} />}>
                              View Results
                            </Button>
                            <Button size="sm" variant="outline" leftIcon={<Icon as={FaEnvelope} />}>
                              Email Results
                            </Button>
                          </HStack>
                        </Box>
                        
                        <Box p={4} bg="blue.50" borderRadius="md" w="full" border="1px" borderColor="blue.200">
                          <HStack justify="space-between">
                            <VStack align="start" spacing={1}>
                              <Text fontWeight="medium">Lipid Panel</Text>
                              <Text fontSize="sm" color="gray.600">Collected: March 14, 2024</Text>
                            </VStack>
                            <Badge colorScheme="blue">Processing</Badge>
                          </HStack>
                          <Text fontSize="sm" color="gray.600" mt={2}>
                            Expected completion: Within 24 hours
                          </Text>
                        </Box>
                        
                        <Box p={4} bg="gray.50" borderRadius="md" w="full" border="1px" borderColor="gray.200">
                          <HStack justify="space-between">
                            <VStack align="start" spacing={1}>
                              <Text fontWeight="medium">Thyroid Panel</Text>
                              <Text fontSize="sm" color="gray.600">Scheduled: March 18, 2024</Text>
                            </VStack>
                            <Badge colorScheme="gray">Scheduled</Badge>
                          </HStack>
                        </Box>
                      </VStack>
                    </VStack>
                  </CardBody>
                </Card>
              </Box>

              <VStack align="start" spacing={6}>
                <VStack align="start" spacing={4}>
                  <Heading size="lg" color={headingColor}>
                    Seamless Results Integration
                  </Heading>
                  <List spacing={4}>
                    <ListItem>
                      <HStack align="start">
                        <ListIcon as={FaRocket} color="green.500" mt={1} />
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="bold" color={headingColor}>Instant Upload</Text>
                          <Text fontSize="sm" color={textColor}>
                            Upload results directly to our platform via API or web portal
                          </Text>
                        </VStack>
                      </HStack>
                    </ListItem>
                    
                    <ListItem>
                      <HStack align="start">
                        <ListIcon as={FaShieldAlt} color="blue.500" mt={1} />
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="bold" color={headingColor}>Secure Access</Text>
                          <Text fontSize="sm" color={textColor}>
                            HIPAA-compliant portal with encrypted patient data access
                          </Text>
                        </VStack>
                      </HStack>
                    </ListItem>
                    
                    <ListItem>
                      <HStack align="start">
                        <ListIcon as={FaEnvelope} color="purple.500" mt={1} />
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="bold" color={headingColor}>Automated Notifications</Text>
                          <Text fontSize="sm" color={textColor}>
                            Patients and mobile phlebotomists get notified when results are ready
                          </Text>
                        </VStack>
                      </HStack>
                    </ListItem>
                    
                    <ListItem>
                      <HStack align="start">
                        <ListIcon as={FaChartLine} color="orange.500" mt={1} />
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="bold" color={headingColor}>Analytics Dashboard</Text>
                          <Text fontSize="sm" color={textColor}>
                            Track turnaround times, patient satisfaction, and volume metrics
                          </Text>
                        </VStack>
                      </HStack>
                    </ListItem>
                  </List>
                </VStack>
              </VStack>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Test Directory Benefits Section */}
      <Box bg="blue.50" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Badge colorScheme="blue" size="lg" px={4} py={2} borderRadius="full">
                Test Directory
              </Badge>
              <Heading size="xl" color={headingColor}>
                Complete Test Directory for Your Customers
              </Heading>
              <Text fontSize="lg" color={textColor} maxW="3xl">
                Your customers can see exactly what tests you offer, cash prices, and insurance accepted - 
                making it easy for them to choose your laboratory for their testing needs.
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">
              {/* Left Column - Features */}
              <VStack align="start" spacing={6}>
                <Heading size="lg" color={headingColor}>
                  What Your Customers See
                </Heading>
                
                <List spacing={4}>
                  <ListItem>
                    <HStack align="start">
                      <ListIcon as={FaFlask} color="blue.500" mt={1} />
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="bold" color={headingColor}>Complete Test Catalog</Text>
                        <Text fontSize="sm" color={textColor}>
                          Every test type you offer with detailed descriptions and requirements
                        </Text>
                      </VStack>
                    </HStack>
                  </ListItem>
                  
                  <ListItem>
                    <HStack align="start">
                      <ListIcon as={FaDollarSign} color="green.500" mt={1} />
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="bold" color={headingColor}>Transparent Cash Pricing</Text>
                        <Text fontSize="sm" color={textColor}>
                          Clear, upfront pricing so patients know exactly what they'll pay
                        </Text>
                      </VStack>
                    </HStack>
                  </ListItem>
                  
                  <ListItem>
                    <HStack align="start">
                      <ListIcon as={FaShieldAlt} color="purple.500" mt={1} />
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="bold" color={headingColor}>Insurance Acceptance</Text>
                        <Text fontSize="sm" color={textColor}>
                          Which insurance plans you accept for each test type
                        </Text>
                      </VStack>
                    </HStack>
                  </ListItem>
                  
                  <ListItem>
                    <HStack align="start">
                      <ListIcon as={FaCertificate} color="orange.500" mt={1} />
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="bold" color={headingColor}>Lab Certifications</Text>
                        <Text fontSize="sm" color={textColor}>
                          Your accreditations and certifications build trust
                        </Text>
                      </VStack>
                    </HStack>
                  </ListItem>
                </List>
              </VStack>

              {/* Right Column - Sample Directory */}
              <Card bg="white" border="2px" borderColor="blue.200" borderRadius="xl" boxShadow="xl">
                <CardHeader>
                  <HStack justify="space-between">
                    <Text fontWeight="bold" color={headingColor}>Sample Test Directory</Text>
                    <Badge colorScheme="green" size="sm">LIVE EXAMPLE</Badge>
                  </HStack>
                </CardHeader>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    {/* Sample Test 1 */}
                    <Box p={3} bg="gray.50" borderRadius="md">
                      <HStack justify="space-between" mb={2}>
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="bold" color={headingColor}>Complete Blood Count (CBC)</Text>
                          <Text fontSize="sm" color={textColor}>Code: 85025</Text>
                        </VStack>
                        <VStack align="end" spacing={0}>
                          <Text fontWeight="bold" color="green.600" fontSize="lg">$45</Text>
                          <Text fontSize="xs" color={textColor}>Cash Price</Text>
                        </VStack>
                      </HStack>
                      <HStack spacing={2}>
                        <Badge colorScheme="blue" size="sm">MOST INSURANCE</Badge>
                        <Badge colorScheme="purple" size="sm">MEDICARE</Badge>
                      </HStack>
                    </Box>

                    {/* Sample Test 2 */}
                    <Box p={3} bg="gray.50" borderRadius="md">
                      <HStack justify="space-between" mb={2}>
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="bold" color={headingColor}>Lipid Panel</Text>
                          <Text fontSize="sm" color={textColor}>Code: 80061</Text>
                        </VStack>
                        <VStack align="end" spacing={0}>
                          <Text fontWeight="bold" color="green.600" fontSize="lg">$65</Text>
                          <Text fontSize="xs" color={textColor}>Cash Price</Text>
                        </VStack>
                      </HStack>
                      <HStack spacing={2}>
                        <Badge colorScheme="blue" size="sm">MOST INSURANCE</Badge>
                        <Badge colorScheme="green" size="sm">SELF PAY</Badge>
                      </HStack>
                    </Box>

                    {/* Sample Test 3 */}
                    <Box p={3} bg="gray.50" borderRadius="md">
                      <HStack justify="space-between" mb={2}>
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="bold" color={headingColor}>Thyroid Stimulating Hormone</Text>
                          <Text fontSize="sm" color={textColor}>Code: 84443</Text>
                        </VStack>
                        <VStack align="end" spacing={0}>
                          <Text fontWeight="bold" color="green.600" fontSize="lg">$55</Text>
                          <Text fontSize="xs" color={textColor}>Cash Price</Text>
                        </VStack>
                      </HStack>
                      <HStack spacing={2}>
                        <Badge colorScheme="blue" size="sm">MOST INSURANCE</Badge>
                        <Badge colorScheme="purple" size="sm">MEDICARE</Badge>
                        <Badge colorScheme="orange" size="sm">MEDICAID</Badge>
                      </HStack>
                    </Box>
                  </VStack>
                </CardBody>
              </Card>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Mobile Phlebotomist Integration Section */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={12}>
          <VStack spacing={4} textAlign="center">
            <Badge colorScheme="purple" size="lg" px={4} py={2} borderRadius="full">
              Mobile Phlebotomist Network
            </Badge>
            <Heading size="xl" color={headingColor}>
              Get Selected for Blood Draws from Our Mobile Network
            </Heading>
            <Text fontSize="lg" color={textColor} maxW="3xl">
              When mobile phlebotomists in our network collect blood samples, your laboratory gets 
              prioritized based on your plan level, location, and test capabilities.
            </Text>
          </VStack>

          {/* Process Steps */}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="full">
            <Card bg={cardBg} borderColor={borderColor} textAlign="center" h="full">
              <CardBody p={6}>
                <VStack spacing={4}>
                  <Box bg="blue.100" p={4} borderRadius="full">
                    <Icon as={FaUsers} boxSize={8} color="blue.600" />
                  </Box>
                  <VStack spacing={2}>
                    <Text fontWeight="bold" color="blue.600" fontSize="sm">STEP 1: COLLECTION</Text>
                    <Heading size="md" color={headingColor}>Mobile Phlebotomist</Heading>
                    <Text color={textColor} fontSize="sm">
                      Collects blood sample from patient at home or office
                    </Text>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>

            <Card bg={cardBg} borderColor={borderColor} textAlign="center" h="full">
              <CardBody p={6}>
                <VStack spacing={4}>
                  <Box bg="purple.100" p={4} borderRadius="full">
                    <Icon as={FaSearch} boxSize={8} color="purple.600" />
                  </Box>
                  <VStack spacing={2}>
                    <Text fontWeight="bold" color="purple.600" fontSize="sm">STEP 2: SELECTION</Text>
                    <Heading size="md" color={headingColor}>Lab Selection</Heading>
                    <Text color={textColor} fontSize="sm">
                      System selects your lab based on placement priority, location, and test type
                    </Text>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>

            <Card bg={cardBg} borderColor={borderColor} textAlign="center" h="full">
              <CardBody p={6}>
                <VStack spacing={4}>
                  <Box bg="green.100" p={4} borderRadius="full">
                    <Icon as={FaFlask} boxSize={8} color="green.600" />
                  </Box>
                  <VStack spacing={2}>
                    <Text fontWeight="bold" color="green.600" fontSize="sm">STEP 3: PROCESSING</Text>
                    <Heading size="md" color={headingColor}>Sample Processing</Heading>
                    <Text color={textColor} fontSize="sm">
                      You receive the sample with optimized FedEx shipping and process results
                    </Text>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Placement Priority Visualization */}
          <Box w="full">
            <VStack spacing={6} textAlign="center">
              <Heading size="lg" color={headingColor}>
                How Placement Priority Works
              </Heading>
              
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} w="full">
                <VStack spacing={4}>
                  <Badge colorScheme="blue" size="lg" px={4} py={2} borderRadius="full">
                    STANDARD
                  </Badge>
                  <Text fontSize="2xl" fontWeight="bold" color="blue.600">$99/month</Text>
                  <Text color={textColor} fontSize="sm">Standard placement in selection algorithm</Text>
                  <Box w="full" bg="gray.200" borderRadius="full" h={4}>
                    <Box w="30%" bg="blue.500" borderRadius="full" h="full" />
                  </Box>
                  <Text fontSize="sm" color="blue.600" fontWeight="bold">30% selection priority</Text>
                </VStack>

                <VStack spacing={4}>
                  <Badge colorScheme="purple" size="lg" px={4} py={2} borderRadius="full">
                    PRIORITY
                  </Badge>
                  <Text fontSize="2xl" fontWeight="bold" color="purple.600">$299/month</Text>
                  <Text color={textColor} fontSize="sm">Higher placement with preferred status</Text>
                  <Box w="full" bg="gray.200" borderRadius="full" h={4}>
                    <Box w="65%" bg="purple.500" borderRadius="full" h="full" />
                  </Box>
                  <Text fontSize="sm" color="purple.600" fontWeight="bold">65% selection priority</Text>
                </VStack>

                <VStack spacing={4}>
                  <Badge colorScheme="orange" size="lg" px={4} py={2} borderRadius="full">
                    HIGHEST
                  </Badge>
                  <Text fontSize="2xl" fontWeight="bold" color="orange.600">$999/month</Text>
                  <Text color={textColor} fontSize="sm">Maximum priority for all new draws</Text>
                  <Box w="full" bg="gray.200" borderRadius="full" h={4}>
                    <Box w="95%" bg="orange.500" borderRadius="full" h="full" />
                  </Box>
                  <Text fontSize="sm" color="orange.600" fontWeight="bold">95% selection priority</Text>
                </VStack>
              </SimpleGrid>
            </VStack>
          </Box>
        </VStack>
      </Container>

      {/* Contact Section */}
      <Box bg={cardBg} borderTop="1px" borderColor={borderColor} py={12}>
        <Container maxW="container.xl">
          <VStack spacing={8} textAlign="center">
            <Heading size="lg" color={headingColor}>
              Questions? We're Here to Help
            </Heading>
            <Text color={textColor} maxW="2xl">
              Our partnership team is available to answer any questions about joining our network.
            </Text>
            <HStack spacing={6}>
              <Button
                leftIcon={<Icon as={FaPhone} />}
                colorScheme="blue"
                variant="outline"
                onClick={() => window.location.href = 'tel:+1234567890'}
              >
                Call Us
              </Button>
              <Button
                leftIcon={<Icon as={FaEnvelope} />}
                colorScheme="blue"
                onClick={() => window.location.href = 'mailto:partnerships@company.com'}
              >
                Email Us
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>
    </Box>
  );
}
